import { Global } from "@emotion/react";
import React, { useEffect, useState } from "react";
import { styled } from "../../styled/styled";
import { darkTheme } from "../dark/dark";
import { lightTheme } from "../light/light";
import { responsiveValues } from "../responsiveValues";
import type { Context as ThemeContext } from "../themeContext/themeContext";
import { getColor, ThemeContextProvider } from "../themeContext/themeContext";
import type { Theme as ThemeType } from "../types/theme";
import type { ThemeName } from "../types/themeName";

export type CSSvariableLocations = "root" | "wrapper";

interface Props {
  children: React.ReactNode;
  key?: string;
  theme: ThemeName;
  cssVariableLocation?: CSSvariableLocations;
}

export function ThemeProvider({
  theme: themeName,
  children,
  cssVariableLocation = "wrapper",
}: Props): React.JSX.Element {
  const [themeContext, setThemeContext] = useState<ThemeContext>({
    themeName,
    getColor,
  });
  const themeDeclaration = themeName === "dark" ? darkTheme : lightTheme;

  useEffect(() => {
    // Make sure we down update the state twice (both before and after the first render)
    if (themeContext.themeName !== themeName) {
      setThemeContext((context) => ({ ...context, themeName }));
    }
  }, [themeName]);

  const cssVariables = { ...themeDeclaration, ...responsiveValues };

  return (
    <ThemeContextProvider value={themeContext}>
      {cssVariableLocation === "root"
        ? (
          <div>
            <Global styles={{ html: cssVariables }} />
            {children}
          </div>
        )
        : <ThemeRoot declaration={themeDeclaration} children={children} />}
    </ThemeContextProvider>
  );
}

const ThemeRoot = styled.div<{ declaration: ThemeType }>(
  /**
   * CSS variables is not valid css keys in the current typescript definition.
   * And since we can't add template literals as key (eg. [`--${string}`]), we only have
   * two options. 1. manually add all css variables as keys or 2. add `any` here.
   */
  (p) => ({
    ...p.declaration,
    ...responsiveValues,
  }),
);
